import { useState, useEffect, useCallback } from "react";
import { Pagination, useToast, Toast } from "@hiya/ui";
import { listWorkflowNotifications, markNotificationsRead } from "../../../api/notifications";
import type { WorkflowNotification } from "../../../types/notification";

const PAGE_SIZE = 10;

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

interface NotificationItemProps {
  notification: WorkflowNotification;
}

function NotificationItem({ notification }: Readonly<NotificationItemProps>) {
  const isUnread = notification.status === "unread";

  return (
    <div className="flex flex-col gap-2 p-6 border-b border-stroke-primary">
      <div className="flex gap-3 items-center">
        <p
          className={`flex-1 text-base font-medium leading-[1.2] ${
            isUnread ? "text-brand-brown" : "text-text-primary"
          }`}
        >
          {notification.title}
        </p>
        {isUnread && (
          <span className="shrink-0 size-2 rounded-full bg-brand-brown" />
        )}
      </div>
      <div className="flex gap-3 items-start text-sm leading-[1.5]">
        <p className="flex-1 text-text-secondary">
          {notification.description}
        </p>
        <p className="shrink-0 text-text-tertiary text-center whitespace-nowrap">
          {formatDate(notification.created_at)}
        </p>
      </div>
    </div>
  );
}

interface NotificationListProps {
  items: WorkflowNotification[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function NotificationList({ items, page, totalPages, onPageChange }: Readonly<NotificationListProps>) {
  return (
    <>
      <div className="flex flex-col">
        {items.map((notification) => (
          <NotificationItem key={notification.id} notification={notification} />
        ))}
      </div>
      <div className="mt-5">
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
      </div>
    </>
  );
}

export function NotificationsPage() {
  const [items, setItems] = useState<WorkflowNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const { toast, showToast } = useToast();

  const loadNotifications = useCallback(async (p: number) => {
    try {
      setLoading(true);
      const data = await listWorkflowNotifications(p, PAGE_SIZE);
      setItems(data.items);
      setTotal(data.total);
    } catch {
      showToast("Failed to load notifications", "warning");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadNotifications(page);
  }, [loadNotifications, page]);

  // Mark notifications as read when page is opened
  useEffect(() => {
    markNotificationsRead().catch(() => {
      // non-critical; fail silently
    });
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <Toast message={toast.message} visible={toast.visible} variant={toast.variant} />

      {loading && (
        <div className="flex flex-col">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-[80px] bg-bg-secondary animate-pulse mb-4 rounded-xl"
            />
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center py-20">
          <p className="text-text-secondary text-base">No notifications yet</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <NotificationList
          items={items}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
